import React, { Dispatch, SetStateAction, useRef, useEffect, KeyboardEvent } from 'react'
import './PinCodeContainer.scss'
import classNames from 'classnames'
import Text from '@components/Text/Text'
import { TextTag } from '@enums/textTag'
import { VelvetSDK } from 'sdk'
import { KeyName } from '@constants/index'

interface PinCodeContainerProps {
  setCode: Dispatch<SetStateAction<string>>
  shouldOutlineBeRed: boolean
}

export const PinCodeContainer = ({ setCode, shouldOutlineBeRed }: PinCodeContainerProps) => {
  const pinLength = 4
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const V = VelvetSDK.Vocabulary.vocabulary

  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return
    const newValue = value.slice(-1)

    // Clear and update the input value
    if (inputRefs.current[index]) {
      inputRefs.current[index]!.value = newValue
    }

    // Collect all values and update code
    const code = inputRefs.current.map((input) => input?.value || '').join('')
    setCode(code)

    // Move to next input if value entered
    if (newValue && index < pinLength - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [KeyName.Backspace, KeyName.ArrowLeft, KeyName.ArrowRight]

    // If it's not a number and not an allowed key, prevent it
    if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
      e.preventDefault()
      return
    }

    // Handle backspace - always delete the last filled input
    if (e.key === KeyName.Backspace) {
      e.preventDefault()

      // Find the last filled input
      let lastFilledIndex = -1
      for (let i = pinLength - 1; i >= 0; i--) {
        if (inputRefs.current[i]?.value) {
          lastFilledIndex = i
          break
        }
      }

      // If there's a filled input, clear it and focus on it
      if (lastFilledIndex >= 0) {
        inputRefs.current[lastFilledIndex]!.value = ''
        inputRefs.current[lastFilledIndex]?.focus()

        // Update code
        const code = inputRefs.current.map((input) => input?.value || '').join('')
        setCode(code)
      }
      return
    }

    // Handle numeric keys - clear before typing
    if (/^\d$/.test(e.key)) {
      e.preventDefault()
      // Clear the current input before setting new value
      if (inputRefs.current[index]) {
        inputRefs.current[index]!.value = e.key
      }

      // Collect all values and update code
      const code = inputRefs.current.map((input) => input?.value || '').join('')
      setCode(code)

      // Move to next input
      if (index < pinLength - 1) {
        inputRefs.current[index + 1]?.focus()
      }
      return
    }
  }

  return (
    <div className="pin-code-container">
      <div className="code-inputs">
        {Array.from({ length: pinLength }).map((_, index) => (
          <input
            key={index}
            className={classNames('pin-input', 'focusable', shouldOutlineBeRed && 'error')}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            tabIndex={-1}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            autoComplete="off"
          />
        ))}
      </div>
      {shouldOutlineBeRed && (
        <Text tag={TextTag.SPAN} className="error-msg">
          {V.profilePinErrorMessage}
        </Text>
      )}
    </div>
  )
}
